import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';
import { eddSourceLabel } from './EddHistory';

const API_URL = getApiUrl();

const PaymentLinkModal = ({ lead, pricingConfigs, onClose, onSuccess, defaultProductType }) => {
  const today = new Date();
  const defaultDueDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  // Trial / product links carry no course date — the backend only needs a non-empty string.
  const placeholderDate = defaultDueDate.toISOString().split('T')[0];
  // Prenatal: the date IS the learner's EDD. There is ONE EDD per mom (lead.due_date) — the modal
  // always picks it up; the rep can confirm a new one, which then overrules it everywhere.
  const initialEdd = (lead.due_date || '').slice(0, 10);

  const [leadEdd, setLeadEdd] = useState(initialEdd);
  const [eddSource, setEddSource] = useState(lead.edd_source || '');

  const [formData, setFormData] = useState({
    lead_id: lead.lead_id,
    product_type: defaultProductType || 'prenatal',
    currency: 'INR',
    tenure_months: 1,
    tenure_days: 30,
    due_date: defaultProductType === 'postnatal' ? '' : initialEdd,
    payment_notes: '',
    use_prorata: true,
    is_till_delivery: false,
    trial_amount: 99,
    final_sales_amount: '',
    // V17 — product links + Elite add-ons
    product: null,          // null (Elite/trial) | 'soulai_coach' | 'baby_tracker'
    plan: 'monthly',
    product_price_override: '',
    product_price_override_reason: '',
    addons: { soulai_coach: false, baby_tracker: false, diet_chart: false },
  });
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isTrialClass = formData.product_type === 'trial_class';
  const isPostnatal = !formData.product && formData.product_type === 'postnatal';
  const isProduct = !!formData.product;
  const PRODUCT_PLANS = { soulai_coach: ['monthly', 'till_delivery'], baby_tracker: ['monthly', 'yearly'] };
  const productPreview = () => {
    if (!isProduct || !selectedPricing) return null;
    const p = selectedPricing;
    if (formData.product === 'soulai_coach') {
      const monthly = Number(p.soulai_coach_monthly_price || 0);
      if (formData.plan === 'monthly') return { amount: monthly, list: monthly, label: '30 days access' };
      const edd = formData.due_date || leadEdd;
      if (!edd) return { amount: 0, list: 0, label: 'Enter the EDD below to price Till-Delivery' };
      const until = new Date(edd); until.setDate(until.getDate() + 3);
      const days = Math.ceil((until - new Date()) / 86400000);
      if (days <= 0) return { amount: 0, list: 0, label: 'EDD has passed — till-delivery unavailable' };
      const months = Math.max(1, Math.ceil(days / 30));
      // V27 — flat Till-Delivery offer (mirrors backend utils.products.soulai_td_offer)
      const mode = p.soulai_till_delivery_display_as || 'percent';
      const fixed = Number(p.soulai_till_delivery_effective_price) || 0;
      const disc = Number(p.soulai_till_delivery_discount_pct) || 0;
      const effective = mode === 'price' && fixed ? fixed : Math.round(monthly * (1 - disc / 100));
      const effPct = monthly ? Math.round((1 - effective / monthly) * 100) : 0;
      return { amount: Math.round(effective * months), list: Math.round(monthly * months), label: `${months} months to EDD+3d · ${effPct}% off (${effective}/mo)` };
    }
    const amt = formData.plan === 'yearly' ? Number(p.baby_tracker_yearly_price || 0) : Number(p.baby_tracker_monthly_price || 0);
    return { amount: amt, list: amt, label: formData.plan === 'yearly' ? '12 months access' : '30 days access' };
  };

  useEffect(() => {
    const pricing = pricingConfigs.find(p => p.currency === formData.currency && p.active_status);
    setSelectedPricing(pricing);
  }, [formData.currency, pricingConfigs]);

  // Callers from dashboard endpoints pass a thin lead (no due_date) — fetch the canonical
  // lead so the stored EDD is still picked up instead of making the rep retype it.
  useEffect(() => {
    if (initialEdd || !lead.lead_id) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/leads/${lead.lead_id}`, { withCredentials: true })
      .then((res) => {
        const d = (res.data?.due_date || '').slice(0, 10);
        if (cancelled || !d) return;
        setLeadEdd(d);
        setEddSource(res.data?.edd_source || '');
        setFormData(prev => (prev.due_date || prev.product_type === 'postnatal' ? prev : { ...prev, due_date: d }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.lead_id]);

  useEffect(() => {
    if (formData.use_prorata && !formData.due_date) {
      setFormData(prev => ({ ...prev, tenure_days: 0, tenure_months: 0 }));
      return;
    }
    if (formData.use_prorata && formData.due_date) {
      const dueDate = new Date(formData.due_date);
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays > 0) setFormData(prev => ({ ...prev, tenure_days: diffDays, tenure_months: Math.max(1, Math.round(diffDays / 30 * 100) / 100) }));
    }
  }, [formData.due_date, formData.use_prorata]);

  // When tenure/pricing changes, reset final_sales_amount to the full price
  useEffect(() => {
    if (!isTrialClass && selectedPricing) {
      const monthlyPrice = selectedPricing.default_monthly_price;
      const dailyRate = monthlyPrice / 30;
      const originalBase = formData.use_prorata ? dailyRate * formData.tenure_days : monthlyPrice * formData.tenure_months;
      const isINR = formData.currency === 'INR';
      const fullTotal = isINR ? Math.round(originalBase * 1.18) : Math.round(originalBase);
      setFormData(prev => ({ ...prev, final_sales_amount: fullTotal }));
    }
  }, [formData.tenure_months, formData.tenure_days, formData.use_prorata, formData.currency, selectedPricing, isTrialClass]);

  const calculateAmounts = () => {
    if (isTrialClass) return { originalBase: formData.trial_amount, base: formData.trial_amount, discount: 0, gst: 0, total: formData.trial_amount, dailyRate: 0 };
    if (!selectedPricing) return { originalBase: 0, base: 0, discount: 0, gst: 0, total: 0, dailyRate: 0 };

    const monthlyPrice = selectedPricing.default_monthly_price;
    const dailyRate = monthlyPrice / 30;
    const originalBase = formData.use_prorata ? dailyRate * formData.tenure_days : monthlyPrice * formData.tenure_months;

    const finalAmount = parseFloat(formData.final_sales_amount) || 0;
    const isINR = formData.currency === 'INR';

    // Back-calculate from final sales amount
    let baseAfterDiscount, gst;
    if (isINR) {
      baseAfterDiscount = finalAmount / 1.18;
      gst = finalAmount - baseAfterDiscount;
    } else {
      baseAfterDiscount = finalAmount;
      gst = 0;
    }

    const discount = originalBase - baseAfterDiscount;

    return {
      originalBase: Math.round(originalBase),
      base: Math.round(baseAfterDiscount * 100) / 100,
      discount: Math.round(discount * 100) / 100,
      gst: Math.round(gst * 100) / 100,
      total: Math.round(finalAmount),
      dailyRate: Math.round(dailyRate),
    };
  };

  const amounts = calculateAmounts();

  // Product links: the catalogue price, plus the rep's optional override (discounts only).
  const catalogueAmount = productPreview()?.amount || 0;
  const listAmount = productPreview()?.list || catalogueAmount;
  const overrideAmount = parseFloat(formData.product_price_override) || 0;
  const overrideError = overrideAmount > 0 && overrideAmount > listAmount
    ? `Cannot charge more than the list price (${formData.currency === 'INR' ? '₹' : '$'}${listAmount.toLocaleString()})`
    : null;
  const productChargeAmount = overrideAmount > 0 ? overrideAmount : catalogueAmount;

  const handleGenerate = async () => {
    if (!isTrialClass && !selectedPricing) { toast.error('Please configure pricing for this currency first'); return; }
    if (isPostnatal && !formData.due_date) { toast.error('Please pick the course expiry date'); return; }
    if (!isProduct && !isTrialClass && !isPostnatal && !formData.due_date) { toast.error('Please enter the Expected Due Date (EDD)'); return; }
    if (isProduct) {
      const pv = productPreview();
      if (!pv || pv.amount <= 0) { toast.error(pv?.label || 'Product price unavailable'); return; }
      if (overrideError) { toast.error(overrideError); return; }
    } else if (!isTrialClass && amounts.total <= 0) { toast.error('Final sales amount must be greater than 0'); return; }
    setLoading(true);
    try {
      const submitData = isProduct
        ? { lead_id: formData.lead_id, currency: formData.currency, product: formData.product, plan: formData.plan,
            product_type: `${formData.product}_${formData.plan}`, due_date: formData.due_date || placeholderDate, payment_notes: formData.payment_notes,
            expected_due_date: (formData.product === 'soulai_coach' && formData.plan === 'till_delivery' ? (formData.due_date || leadEdd) : leadEdd) || undefined, total_amount: productChargeAmount, tenure_months: 1,
            product_price_override: overrideAmount > 0 ? overrideAmount : undefined,
            product_price_override_reason: overrideAmount > 0 ? (formData.product_price_override_reason || undefined) : undefined }
        : isTrialClass
        ? { lead_id: formData.lead_id, currency: formData.currency, total_amount: formData.trial_amount, description: 'Trial Class', product_type: 'trial_class', due_date: formData.due_date || placeholderDate, payment_notes: formData.payment_notes }
        : {
            lead_id: formData.lead_id,
            currency: formData.currency,
            tenure_months: formData.use_prorata ? formData.tenure_months : Math.round(formData.tenure_months),
            tenure_days: formData.tenure_days,
            use_prorata: formData.use_prorata,
            is_till_delivery: isPostnatal ? false : formData.is_till_delivery,
            course_expiry_date: isPostnatal ? formData.due_date : undefined,
            discount_amount: amounts.discount > 0 ? Math.round(amounts.discount * 100) / 100 : 0,
            total_amount: amounts.total,
            due_date: formData.due_date,
            payment_notes: formData.payment_notes,
            product_type: formData.product_type,
            addons: (formData.addons.soulai_coach || formData.addons.baby_tracker || formData.addons.diet_chart) ? formData.addons : undefined,
          };
      const response = await axios.post(`${API_URL}/api/payments/create-link`, submitData);
      setGeneratedLink(response.data);
      toast.success('Payment link generated successfully');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : `Failed to generate payment link${error.response?.status ? ` (HTTP ${error.response.status})` : ''}`);
    }
    finally { setLoading(false); }
  };

  const copyToClipboard = async () => {
    if (!generatedLink?.razorpay_short_url) return;
    try { await navigator.clipboard.writeText(generatedLink.razorpay_short_url); setCopied(true); toast.success('Link copied to clipboard'); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error('Failed to copy link'); }
  };

  const uniqueCurrencies = [...new Set(pricingConfigs.filter(p => p.active_status).map(p => p.currency))];
  // CAD is always offered — Canada has no price list yet, so the rep types the amount in
  // "Charge this instead" (founder call, Oct 2026).
  const availableCurrencies = [...new Set([...(uniqueCurrencies.length > 0 ? uniqueCurrencies : ['INR', 'USD', 'EUR', 'GBP']), 'CAD'])];

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-900">{lead.enrollment_status === 'enrolled' ? 'Generate Renewal Payment Link' : 'Generate Payment Link'}</h2>
            <p className="text-sm text-slate-500">For {lead.full_name}</p>
            {lead.enrollment_status === 'enrolled' && <p className="text-xs text-emerald-600 mt-1">Currently enrolled - This will be a renewal payment</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!generatedLink ? (
            <div className="space-y-4">
              <div data-testid="link-product-selector">
                <label className="block text-sm font-medium text-slate-700 mb-2">What are you selling? *</label>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[{ value: null, label: 'Elite / Trial' }, { value: 'soulai_coach', label: 'SoulAI Coach' }, { value: 'baby_tracker', label: 'Baby Tracker' }].map((o) => (
                    <button key={String(o.value)} type="button" onClick={() => setFormData({ ...formData, product: o.value, plan: 'monthly', product_price_override: '', product_price_override_reason: '' })}
                      className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${formData.product === o.value ? 'bg-primary text-white border-primary' : 'bg-white text-slate-700 border-slate-300 hover:border-primary/60'}`}
                      data-testid={`link-product-${o.value || 'elite'}`}>{o.label}</button>
                  ))}
                </div>
              </div>

              {isProduct && (
                <div className="space-y-3 rounded-lg border border-border p-3" data-testid="product-plan-block">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Plan *</label>
                      <select value={formData.plan} onChange={(e) => setFormData({ ...formData, plan: e.target.value, product_price_override: '' })} className="input-base" data-testid="product-plan">
                        {PRODUCT_PLANS[formData.product].map((pl) => <option key={pl} value={pl}>{pl.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Currency *</label>
                      <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="input-base" data-testid="product-currency">
                        <option value="INR">INR</option><option value="USD">USD ($)</option><option value="CAD">CAD (C$)</option>
                      </select>
                    </div>
                  </div>
                  {formData.product === 'soulai_coach' && formData.plan === 'till_delivery' && (
                    <div data-testid="product-edd-block">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Expected Due Date (EDD) *</label>
                      <input type="date" value={formData.due_date} min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value, product_price_override: '' })}
                        className="input-base" data-testid="product-edd" />
                      {leadEdd ? (
                        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="product-edd-source">
                          {formData.due_date === leadEdd
                            ? `Picked up from her profile${eddSource ? ` (${eddSourceLabel(eddSource)})` : ''} — change it only if she has confirmed a new date.`
                            : 'This replaces her stored EDD everywhere and is sent to the LMS.'}
                        </p>
                      ) : (
                        <p className="mt-1 text-[11px] text-muted-foreground">This lead has no EDD yet — it will be saved to her profile and sent to the LMS.</p>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
                    <span className="text-xs text-muted-foreground">{productPreview()?.label}</span>
                    <span className="font-display text-lg font-bold" data-testid="product-amount">{formData.currency === 'INR' ? '₹' : '$'}{(catalogueAmount || 0).toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Charge this instead (optional)</label>
                      <input type="number" min="1" step="1" value={formData.product_price_override}
                        onChange={(e) => setFormData({ ...formData, product_price_override: e.target.value })}
                        placeholder={String(catalogueAmount || '')}
                        className="input-base" data-testid="product-price-override" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Why (optional)</label>
                      <input type="text" value={formData.product_price_override_reason}
                        onChange={(e) => setFormData({ ...formData, product_price_override_reason: e.target.value })}
                        placeholder="Festive offer, goodwill…" className="input-base"
                        data-testid="product-price-override-reason" />
                    </div>
                  </div>
                  {overrideAmount > 0 && (
                    overrideError
                      ? <p className="text-xs font-medium text-rose-600" data-testid="product-override-error">{overrideError}</p>
                      : <p className="text-xs font-medium text-emerald-700" data-testid="product-override-summary">
                          Charging {formData.currency === 'INR' ? '₹' : '$'}{overrideAmount.toLocaleString()} instead of {formData.currency === 'INR' ? '₹' : '$'}{catalogueAmount.toLocaleString()}
                          {listAmount > 0 ? ` · ${Math.round((1 - overrideAmount / listAmount) * 100)}% off list` : ''}
                        </p>
                  )}
                  <p className="text-[11px] text-muted-foreground">Leave the override blank to use the Pricing → SoulAI / Baby Tracker price. An override is recorded against your name in the audit log and cannot exceed the list price. LMS grants the same access either way.</p>
                </div>
              )}

              {!isProduct && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Course Type *</label>
                <div className="grid grid-cols-3 gap-2">
                  {(lead.enrollment_status === 'enrolled'
                    ? [{ value: 'prenatal', label: 'Prenatal Renewal' }, { value: 'postnatal', label: 'Postnatal Renewal' }, { value: 'trial_class', label: 'Trial Class' }]
                    : [{ value: 'trial_class', label: 'Trial Class' }, { value: 'prenatal', label: 'Prenatal' }, { value: 'postnatal', label: 'Postnatal' }]
                  ).map(option => (
                    <button key={option.value} type="button" onClick={() => setFormData({
                      ...formData,
                      product_type: option.value,
                      // Postnatal has no EDD — sales picks the course expiry, so start blank.
                      // Prenatal — the date is the EDD: prefill from the lead, never a made-up default.
                      due_date: option.value === 'postnatal' ? '' : (formData.due_date || leadEdd),
                      is_till_delivery: option.value === 'postnatal' ? false : formData.is_till_delivery,
                      use_prorata: option.value === 'postnatal' ? true : formData.use_prorata,
                    })}
                      className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${formData.product_type === option.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'}`}
                      data-testid={`product-type-${option.value}`}>{option.label}</button>
                  ))}
                </div>
              </div>
              )}

              {!isProduct && !isTrialClass && (
                <div className="rounded-lg border border-border p-3" data-testid="elite-addons">
                  <p className="text-sm font-medium text-slate-700 mb-2">Include with Elite (add-ons granted by LMS on payment)</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={formData.addons.soulai_coach} onChange={(e) => setFormData({ ...formData, addons: { ...formData.addons, soulai_coach: e.target.checked } })} data-testid="addon-soulai" /> SoulAI Coach</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={formData.addons.baby_tracker} onChange={(e) => setFormData({ ...formData, addons: { ...formData.addons, baby_tracker: e.target.checked } })} data-testid="addon-baby" /> Baby Tracker</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={formData.addons.diet_chart} onChange={(e) => setFormData({ ...formData, addons: { ...formData.addons, diet_chart: e.target.checked } })} data-testid="addon-diet" /> Diet Chart</label>
                  </div>
                  {formData.addons.diet_chart && (
                    <p className="mt-2 text-[11px] text-muted-foreground" data-testid="addon-diet-note">
                      The LMS dietician will be told she has a diet plan due — {lead.country === 'India' ? 'India' : 'international'} plan ({lead.country || 'country not set'}). Shown as <span className="font-medium">Complimentary</span> in her payment history unless you charge for it in the amount above.
                    </p>
                  )}
                </div>
              )}

              {isProduct ? null : isTrialClass ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Currency *</label>
                      <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="input-base" data-testid="trial-currency">
                        <option value="INR">INR</option><option value="USD">USD ($)</option><option value="EUR">EUR</option><option value="GBP">GBP</option><option value="CAD">CAD (C$)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                      <input type="number" min="1" value={formData.trial_amount} onChange={(e) => setFormData({ ...formData, trial_amount: parseInt(e.target.value) || 99 })} className="input-base" data-testid="trial-amount" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
                    <input type="text" value={formData.payment_notes} onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })} className="input-base" placeholder="Optional notes..." data-testid="trial-notes" />
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 mt-4">
                    <div className="flex justify-between items-center"><span className="font-semibold text-slate-900">Total Amount</span><span className="text-xl font-bold text-indigo-600">{formData.currency} {formData.trial_amount}</span></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Currency *</label>
                      <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="input-base" data-testid="payment-currency">
                        {availableCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{isPostnatal ? 'Course Expiry Date *' : 'Expected Due Date (EDD) *'}</label>
                      <input type="date" value={formData.due_date} min={isPostnatal ? new Date(Date.now() + 86400000).toISOString().split('T')[0] : undefined}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value, use_prorata: true })} className="input-base" data-testid={isPostnatal ? 'payment-course-expiry-date' : 'payment-due-date'} />
                      <p className="text-xs text-slate-500 mt-1" data-testid="payment-date-hint">
                        {isPostnatal
                          ? 'Course access valid till this date — tenure & amount are auto-calculated, and the LMS suspends access after it'
                          : leadEdd && formData.due_date === leadEdd
                          ? `Picked up from her profile${eddSource ? ` (${eddSourceLabel(eddSource)})` : ''} — change it only if she has confirmed a new date. Tenure runs till this date; the LMS keeps access for 7 days after the EDD.`
                          : "Learner's EDD — this becomes her one EDD everywhere and is sent to the LMS. Tenure runs till this date; the LMS keeps access for 7 days after the EDD before auto-suspending."}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Tenure {formData.use_prorata ? '(Auto-calculated)' : '(Manual)'}</label>
                      <div className="flex items-center gap-2">
                        <input type="number" min="1" max="24" step="0.1" value={formData.use_prorata ? formData.tenure_months.toFixed(2) : formData.tenure_months}
                          onChange={(e) => { const t = parseInt(e.target.value) || 1; setFormData({ ...formData, tenure_months: t, tenure_days: t * 30, use_prorata: false }); }}
                          className="input-base" data-testid="payment-tenure" />
                        <span className="text-sm text-slate-500">months</span>
                      </div>
                      {formData.use_prorata && formData.tenure_days > 0 && <p className="text-xs text-indigo-600 mt-1">{formData.tenure_days} days (pro-rata)</p>}
                      {!formData.due_date && <p className="text-xs text-amber-600 mt-1" data-testid="payment-date-required-hint">{isPostnatal ? 'Pick the course expiry date first' : 'Enter the Expected Due Date first'}</p>}
                      {!isPostnatal && (
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                          <input type="checkbox" checked={formData.is_till_delivery} onChange={(e) => setFormData({ ...formData, is_till_delivery: e.target.checked, use_prorata: e.target.checked ? true : formData.use_prorata })} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-testid="payment-till-delivery" />
                          <span className="text-sm text-slate-600">Till Delivery enrollment</span>
                        </label>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Final Sales Amount *</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.final_sales_amount}
                        onChange={(e) => setFormData({ ...formData, final_sales_amount: e.target.value })}
                        className="input-base"
                        placeholder={`Total ${formData.currency === 'INR' ? '(incl. GST)' : ''}`}
                        data-testid="payment-final-amount"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {formData.currency === 'INR' ? 'GST 18% will be auto-calculated' : 'No GST applicable'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                    <textarea value={formData.payment_notes} onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })} className="input-base h-20" placeholder="Optional notes..." />
                  </div>
                  {selectedPricing && (
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-slate-600">Monthly Fee</span><span className="font-medium">{formData.currency} {selectedPricing.default_monthly_price.toLocaleString()}</span></div>
                      {formData.use_prorata && <div className="flex justify-between text-sm"><span className="text-slate-600">Daily Rate</span><span className="font-medium">{formData.currency} {amounts.dailyRate.toLocaleString()}/day</span></div>}
                      <div className="flex justify-between text-sm"><span className="text-slate-600">Original Price ({formData.use_prorata ? `${formData.tenure_days} days` : `${formData.tenure_months} months`})</span><span className="font-medium">{formData.currency} {amounts.originalBase.toLocaleString()}</span></div>
                      {amounts.discount > 0 && <div className="flex justify-between text-sm text-green-600"><span>Discount</span><span>- {formData.currency} {Math.round(amounts.discount).toLocaleString()}</span></div>}
                      <div className="flex justify-between text-sm"><span className="text-slate-600">Base Amount</span><span className="font-medium">{formData.currency} {Math.round(amounts.base).toLocaleString()}</span></div>
                      {formData.currency === 'INR' && <div className="flex justify-between text-sm"><span className="text-slate-600">GST (18%)</span><span className="font-medium">{formData.currency} {Math.round(amounts.gst).toLocaleString()}</span></div>}
                      <div className="flex justify-between text-base pt-2 border-t border-slate-200"><span className="font-semibold text-slate-900">Final Amount</span><span className="font-bold text-indigo-600">{formData.currency} {amounts.total.toLocaleString()}</span></div>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                <button onClick={handleGenerate} disabled={loading || (!isTrialClass && !selectedPricing)} className="btn-primary" data-testid="generate-payment-link-btn">{loading ? 'Generating...' : 'Generate Link'}</button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CreditCard className="w-8 h-8 text-green-600" /></div>
                <h3 className="text-lg font-semibold text-slate-900">Payment Link Generated!</h3>
                <p className="text-sm text-slate-500 mt-1">Share this link with the customer</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <label className="block text-xs font-medium text-slate-500 mb-2">Payment Link</label>
                <div className="flex items-center gap-2">
                  <input type="text" readOnly value={generatedLink.razorpay_short_url} className="input-base flex-1 bg-white" data-testid="generated-payment-link" />
                  <button onClick={copyToClipboard} className={`px-4 py-2 rounded-md font-medium transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`} data-testid="copy-payment-link-btn">{copied ? 'Copied!' : 'Copy'}</button>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-600">Amount</span><span className="font-medium">{generatedLink.currency} {generatedLink.total_amount.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-600">Due Date</span><span className="font-medium">{generatedLink.due_date}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-600">Status</span><span className="badge-warning px-2 py-0.5 rounded-full text-xs">{generatedLink.payment_status.toUpperCase()}</span></div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button onClick={onSuccess} className="btn-primary">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentLinkModal;
